import axios from 'axios'
import { supabase } from '../lib/supabase'

type CreateApiClientOptions = {
  baseURL: string
  tokenKey: string
  userKey?: string
  loginPath: string
}

const LEGACY_AUTH_STORAGE_KEYS = ['token', 'user'] as const

let cachedToken: string | null = null
let tokenExpiresAt = 0

function clearLegacyAuthStorage(): void {
  for (const key of LEGACY_AUTH_STORAGE_KEYS) {
    localStorage.removeItem(key)
  }
}

export function clearCachedToken(): void {
  cachedToken = null
  tokenExpiresAt = 0
}

export function createApiClient({ baseURL, tokenKey, userKey, loginPath }: CreateApiClientOptions) {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    // 304 Not Modified を例外扱いしない（Vercel Edge が自動付与するETagでの誤throw対策）
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  })

  client.interceptors.request.use(
    async (config) => {
      const now = Date.now()
      // 有効期限の1分前まではキャッシュを利用
      if (cachedToken && now < tokenExpiresAt - 60_000) {
        config.headers.Authorization = `Bearer ${cachedToken}`
        return config
      }

      // Supabase セッションからトークンを取得
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.access_token) {
        cachedToken = session.access_token
        tokenExpiresAt = (session.expires_at ?? 0) * 1000
        config.headers.Authorization = `Bearer ${session.access_token}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        clearCachedToken()
        localStorage.removeItem(tokenKey)
        if (userKey) {
          localStorage.removeItem(userKey)
        }
        clearLegacyAuthStorage()
        window.location.href = loginPath
      }
      return Promise.reject(error)
    }
  )

  return client
}
